import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import msDark from '../assets/ms_light.png';
import msLight from '../assets/ms_dark.png';
import fsDark from '../assets/fs_light.png';
import fsLight from '../assets/fs_dark.png';

const STORAGE_KEY = 'dashboard-fullscreen';

const requestDocumentFullscreen = () => {
  const request = document.documentElement.requestFullscreen;
  if (typeof request === 'function') {
    return request.call(document.documentElement);
  }
  return Promise.resolve();
};

const exitDocumentFullscreen = () => {
  const exit = document.exitFullscreen;
  if (typeof exit === 'function') {
    return exit.call(document);
  }
  return Promise.resolve();
};

export default function FullscreenToggle() {
  const { theme } = useTheme();
  const [isFullscreen, setIsFullscreen] = useState(
    !!document.fullscreenElement
  );

  useEffect(() => {
    const handler = () => {
      const full = !!document.fullscreenElement;
      setIsFullscreen(full);
      if (full) {
        localStorage.setItem(STORAGE_KEY, 'true');
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    const shouldRestore =
      !window.location.pathname.startsWith('/login') &&
      !document.fullscreenElement &&
      localStorage.getItem(STORAGE_KEY) === 'true';

    if (!shouldRestore) {
      return undefined;
    }

    let restored = false;

    const handleUserInteraction = () => {
      if (restored || document.fullscreenElement) return;
      attemptRestore();
    };

    const handleVisibility = () => {
      if (restored || document.fullscreenElement) return;
      attemptRestore();
    };

    const cleanupListeners = () => {
      document.removeEventListener('pointerup', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('visibilitychange', handleVisibility);
    };

    const attemptRestore = () =>
      requestDocumentFullscreen()
        .then(() => {
          restored = true;
          cleanupListeners();
        })
        .catch(() => {
          /* Browsers may block the request without a gesture; keep the flag for the next attempt. */
        });

    attemptRestore();
    document.addEventListener('pointerup', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('visibilitychange', handleVisibility);

    return cleanupListeners;
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      requestDocumentFullscreen().catch(() => {
        localStorage.removeItem(STORAGE_KEY);
      });
    } else {
      exitDocumentFullscreen().catch(() => {
        /* noop */
      });
    }
  };

  const icon = theme === 'dark'
    ? (isFullscreen ? fsDark : msDark)
    : (isFullscreen ? fsLight : msLight);

  return (
    <button
      onClick={toggleFullscreen}
      className="p-2 rounded-full bg-border text-default hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-focus transition-colors"
      aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
    >
      <img
        src={icon}
        alt=""
        className="w-5 h-5 transition-transform duration-200"
      />
    </button>
  );
}
